/**
 * ModoForjaSettings - Compact collapsible settings panel
 */

import React, { useState } from 'react';
import { Settings, ChevronDown, ChevronUp, Infinity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useModoForja } from '@/contexts/ModoForjaContext';

export default function ModoForjaSettings() {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    mode,
    focusDuration,
    breakDuration,
    totalCycles,
    isRunning,
    sessionType,
    setMode,
    setFocusDuration,
    setBreakDuration,
    setTotalCycles,
  } = useModoForja();

  const focusMinutes = Math.floor(focusDuration / 60);
  const breakMinutes = Math.floor(breakDuration / 60);

  const cycleOptions = [2, 4, 6, 8, null]; // null = infinite

  return (
    <div className="w-full">
      {/* Toggle button - compact */}
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={isRunning}
        className={cn(
          "w-full flex items-center justify-between gap-2 px-3 py-1.5 h-auto text-left",
          "text-muted-foreground/60 hover:text-foreground/80 hover:bg-secondary/20",
          "rounded-lg",
          isRunning && "opacity-40 cursor-not-allowed"
        )}
      >
        <div className="flex items-center gap-1.5">
          <Settings className="w-3 h-3" />
          <span className="text-[11px]">
            {mode === 'standard' ? `Padrão (${focusMinutes}/${breakMinutes})` : `${focusMinutes}/${breakMinutes} min`}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
      </Button>

      {/* Settings panel - compact */}
      {isExpanded && !isRunning && (
        <div className="mt-2 p-3 rounded-xl bg-secondary/15 border border-white/[0.04] space-y-3 animate-fade-in">
          {/* Mode selector */}
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Modo</Label>
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                variant={mode === 'standard' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('standard')}
                className={cn(
                  "h-7 text-[10px]",
                  mode === 'standard' 
                    ? sessionType === 'focus' ? "bg-primary" : "bg-success"
                    : "border-white/10"
                )}
              >
                Padrão
              </Button>
              <Button
                variant={mode === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('custom')}
                className={cn(
                  "h-7 text-[10px]",
                  mode === 'custom' 
                    ? sessionType === 'focus' ? "bg-primary" : "bg-success"
                    : "border-white/10"
                )}
              >
                Personalizado
              </Button>
            </div>
          </div>

          {/* Custom durations */}
          {mode === 'custom' && (
            <>
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">
                  Foco: <span className="text-foreground/80">{focusMinutes} min</span>
                </Label>
                <div className="flex items-center gap-1">
                  {[15, 20, 25, 30, 45, 60].map(min => (
                    <Button
                      key={min}
                      variant={focusMinutes === min ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFocusDuration(min)}
                      className={cn(
                        "h-6 px-2 text-[10px] flex-1",
                        focusMinutes === min 
                          ? "bg-primary" 
                          : "border-white/10"
                      )}
                    >
                      {min}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">
                  Descanso: <span className="text-foreground/80">{breakMinutes} min</span>
                </Label>
                <div className="flex items-center gap-1">
                  {[3, 5, 10, 15, 20].map(min => (
                    <Button
                      key={min}
                      variant={breakMinutes === min ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBreakDuration(min)}
                      className={cn(
                        "h-6 px-2 text-[10px] flex-1",
                        breakMinutes === min 
                          ? "bg-success" 
                          : "border-white/10"
                      )}
                    >
                      {min}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Cycle count */}
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">
              Ciclos: <span className="text-foreground/80">{totalCycles ?? '∞'}</span>
            </Label>
            <div className="flex items-center gap-1">
              {cycleOptions.map(cycles => (
                <Button
                  key={cycles ?? 'infinite'}
                  variant={totalCycles === cycles ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTotalCycles(cycles)}
                  className={cn(
                    "h-6 px-2 text-[10px] flex-1",
                    totalCycles === cycles 
                      ? sessionType === 'focus' ? "bg-primary" : "bg-success"
                      : "border-white/10"
                  )}
                >
                  {cycles ?? <Infinity className="w-3.5 h-3.5" />}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
